import React, { useEffect, useState } from "react";
import axios from "axios";
import { Download, RefreshCw, FileText, CheckCircle2, AlertCircle, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { invalidateHelpVisibilityCache } from "../../hooks/useHelpVisibility";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminGuides() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [visibility, setVisibility] = useState(null); // null = loading
  const [togglingVis, setTogglingVis] = useState(false);

  const authHeaders = () => {
    const t = localStorage.getItem("bypassgo_token");
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const loadStatus = async () => {
    try {
      const r = await axios.get(`${API}/api/help/admin/status`, { headers: authHeaders() });
      setStatus(r.data);
    } catch (e) {
      setStatus({ error: "Impossible de charger le statut" });
    }
  };

  const loadVisibility = async () => {
    try {
      const r = await axios.get(`${API}/api/help/visibility`);
      setVisibility(!!r.data?.visible);
    } catch (e) {
      setVisibility(false);
    }
  };

  useEffect(() => {
    loadStatus();
    loadVisibility();
  }, []);

  const toggleVisibility = async (next) => {
    setTogglingVis(true);
    try {
      await axios.post(
        `${API}/api/help/admin/visibility`,
        { enabled: next },
        { headers: authHeaders() },
      );
      setVisibility(next);
      invalidateHelpVisibilityCache();
      toast.success(next ? "Centre d'aide AFFICHÉ sur le site public" : "Centre d'aide MASQUÉ sur le site public");
    } catch (e) {
      toast.error("Erreur lors du basculement");
    } finally {
      setTogglingVis(false);
    }
  };

  const regenerate = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/api/help/admin/regenerate`, {}, { headers: authHeaders() });
      toast.success("Guides PDF régénérés avec succès");
      await loadStatus();
    } catch (e) {
      toast.error("Erreur lors de la régénération");
    } finally {
      setLoading(false);
    }
  };

  const fmtDate = (ts) => {
    if (!ts) return "—";
    return new Date(ts * 1000).toLocaleString("fr-FR");
  };
  const fmtSize = (bytes) => {
    if (!bytes) return "—";
    return `${(bytes / 1024).toFixed(1)} Ko`;
  };

  return (
    <div data-testid="admin-guides-page">
      <header className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Guides PDF</h1>
          <p className="text-sm text-slate-500 mt-1">
            Gérez les guides utilisateur officiels au format PDF.
          </p>
        </div>
        <button
          onClick={regenerate}
          disabled={loading}
          className="inline-flex items-center gap-2 bg-[#002FA7] text-white px-4 py-2.5 rounded-lg font-medium hover:bg-[#001a87] transition disabled:opacity-60"
          data-testid="admin-guides-regenerate-btn"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          {loading ? "Régénération…" : "Régénérer les PDFs"}
        </button>
      </header>

      {/* === Toggle visibilité publique du centre d'aide === */}
      <section
        className={`mb-6 rounded-2xl border p-5 flex items-center gap-4 ${
          visibility ? "bg-green-50 border-green-200" : "bg-slate-50 border-slate-200"
        }`}
        data-testid="help-visibility-section"
      >
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
          visibility ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-600"
        }`}>
          {visibility ? <Eye className="w-6 h-6" /> : <EyeOff className="w-6 h-6" />}
        </div>
        <div className="flex-1">
          <h2 className="text-base font-bold text-slate-900">
            Centre d'aide sur le site public
          </h2>
          <p className="text-sm text-slate-600 mt-0.5">
            {visibility === null
              ? "Chargement…"
              : visibility
              ? "Actuellement AFFICHÉ aux visiteurs et établissements (footer, bouton « ? », pages /help/*)."
              : "Actuellement MASQUÉ aux utilisateurs publics. Seuls les administrateurs peuvent y accéder."}
          </p>
        </div>
        <button
          onClick={() => toggleVisibility(!visibility)}
          disabled={togglingVis || visibility === null}
          className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition disabled:opacity-60 ${
            visibility
              ? "bg-white text-slate-700 border border-slate-300 hover:bg-slate-50"
              : "bg-[#002FA7] text-white hover:bg-[#001a87]"
          }`}
          data-testid="help-visibility-toggle-btn"
        >
          {visibility ? (
            <>
              <EyeOff className="w-4 h-4" /> Masquer
            </>
          ) : (
            <>
              <Eye className="w-4 h-4" /> Afficher
            </>
          )}
        </button>
      </section>

      <div className="grid md:grid-cols-2 gap-6">
        <GuideCard
          title="Guide Visiteur"
          subtitle="Tout pour les visiteurs : recherche, ticket, suivi, notifications."
          color="from-blue-500 to-blue-600"
          status={status?.visitor}
          pdfPath="/api/help/pdf/visitor"
          previewPath="/help/visitor"
          fmtDate={fmtDate}
          fmtSize={fmtSize}
          testid="guide-visitor"
        />
        <GuideCard
          title="Guide Établissement"
          subtitle="Pilotage complet pour les établissements partenaires."
          color="from-indigo-500 to-indigo-600"
          status={status?.establishment}
          pdfPath="/api/help/pdf/establishment"
          previewPath="/help/establishment"
          fmtDate={fmtDate}
          fmtSize={fmtSize}
          testid="guide-establishment"
        />
      </div>

      <div className="mt-8 bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-lg text-sm text-amber-900">
        💡 <strong>Note :</strong> Les sources Markdown des guides se trouvent dans{" "}
        <code className="bg-amber-100 px-1 py-0.5 rounded">/app/docs/guides/</code>.
        Modifiez-les puis cliquez sur « Régénérer les PDFs » pour obtenir les nouvelles
        versions immédiatement.
      </div>
    </div>
  );
}

function GuideCard({ title, subtitle, color, status, pdfPath, previewPath, fmtDate, fmtSize, testid }) {
  const exists = status?.exists;

  return (
    <div
      className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm"
      data-testid={`${testid}-card`}
    >
      <div className={`bg-gradient-to-br ${color} text-white p-6`}>
        <FileText className="w-10 h-10 mb-3 opacity-80" />
        <h2 className="text-xl font-bold">{title}</h2>
        <p className="text-white/80 text-sm mt-1">{subtitle}</p>
      </div>

      <div className="p-5 space-y-3">
        <div className="flex items-center gap-2 text-sm">
          {exists ? (
            <>
              <CheckCircle2 className="w-4 h-4 text-green-600" />
              <span className="text-slate-700">PDF disponible</span>
            </>
          ) : (
            <>
              <AlertCircle className="w-4 h-4 text-amber-600" />
              <span className="text-slate-700">PDF non généré</span>
            </>
          )}
        </div>

        {exists && (
          <dl className="text-xs space-y-1 text-slate-600">
            <div className="flex justify-between">
              <dt>Taille</dt>
              <dd className="font-medium text-slate-900">{fmtSize(status.size_bytes)}</dd>
            </div>
            <div className="flex justify-between">
              <dt>Dernière génération</dt>
              <dd className="font-medium text-slate-900">{fmtDate(status.modified_at)}</dd>
            </div>
          </dl>
        )}

        <div className="flex gap-2 pt-2">
          <a
            href={`${API}${pdfPath}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 inline-flex items-center justify-center gap-2 bg-[#002FA7] text-white px-4 py-2 rounded-lg font-medium hover:bg-[#001a87] transition text-sm"
            data-testid={`${testid}-download-btn`}
          >
            <Download className="w-4 h-4" /> Télécharger
          </a>
          <a
            href={previewPath}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 inline-flex items-center justify-center gap-2 bg-slate-100 text-slate-700 px-4 py-2 rounded-lg font-medium hover:bg-slate-200 transition text-sm"
            data-testid={`${testid}-preview-btn`}
          >
            Aperçu en ligne
          </a>
        </div>
      </div>
    </div>
  );
}
